import CTIDataSharing from "@/assets/images/cti-data-sharing.png";
import { Button } from "@/components/button";
import InputField from "@/components/formElement/InputField";
import ReactListbox from "@/components/formElement/ReactListbox";
import SiteFooter from "@/components/siteFooter";
import SiteHeader from "@/components/siteHeader";
import { useAxiosGet, useAxiosPost } from "@/hooks/useAxios";
import {
  promptDisabledSelector,
  setPromptOpen,
} from "@/redux/slice/promptSlice";
import { setToken } from "@/redux/slice/tokenSlice";
import { PRIVATE_NAVIGATION } from "@/utils/constants";
import { isSubmittable } from "@/utils/helpers";
import { ReactComponent as GuideIcon } from "@assets/icons/guideIcon.svg";
import { ReactComponent as CloseIcon } from "@icons/close-white.svg";
import { Form, Formik, FormikValues } from "formik";
import { useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import AccessDenied from "../AccessDenied";
import { ConsumerInitializeSchema } from "./validations";

interface ConsumerInitialize {
  firstName: string;
  lastName: string;
  email: string;
  recaptcha: string | null;
}

const ConsumerTypes = [
  {
    title: "Sandbox consumer data",
    value: "SANDBOX",
  },
  {
    title: "Real Open Banking data",
    value: "PRODUCTION",
  },
];
const initialValues: ConsumerInitialize = {
  firstName: "",
  lastName: "",
  email: "",
  recaptcha: null,
};

const Guide = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { projectId, projectApiKey } = useParams();
  const location = useLocation();
  const splitLocation = location.pathname.split("/")?.[2];
  const isPromptDisabled = useSelector(promptDisabledSelector);
  const [isError, setIsError] = useState<string>("");
  const [consumerInitializeApi] = useAxiosPost();
  const [verifyConsumerURL] = useAxiosGet();
  const [consumerType, setConsumerType] = useState("SANDBOX");
  const [initialConsumer] = useState<ConsumerInitialize>(initialValues);
  const [invalid, setInvalid] = useState<boolean | null>(null);

  useEffect(() => {
    if (!isPromptDisabled) {
      dispatch(setPromptOpen({ isOpen: true }));
    }
    verifyConsumerLink();
  }, []);

  const onSubmit = async (values: FormikValues) => {
    const data = {
      firstname: values.firstName,
      lastname: values.lastName,
      email: values.email,
      api_key: projectApiKey,
      consumer_type: consumerType,
    };
    const response = await consumerInitializeApi("/consumers/initialize", data);
    if (response?.data?.access_token) {
      dispatch(
        setToken({
          access_token: response?.data?.access_token,
          refresh_token: response?.data?.refresh_token,
        })
      );
      navigate(PRIVATE_NAVIGATION.profile.edit.path);
    } else {
      setIsError(response?.error);
    }
  };

  const verifyConsumerLink = async () => {
    const response = await verifyConsumerURL(
      `/merchant_projects/verify-consumer-url/${projectId}/${projectApiKey}?url_type=${splitLocation === "sandbox" ? "Testing" : "Live"}`
    );
    if (response.data) setInvalid(response.data);
    else setInvalid(false);
  };

  return invalid ? (
    <div className="w-full">
      <SiteHeader />
      <div className="w-full flex items-center justify-center">
        <div className={`lg:w-3/6 xl:w-3/6 w-3/6 mt-6`}>
          {isError && (
            <div className="gap-4 mb-4">
              <div>
                <span
                  className={`text-body-black font-medium bg-red-alert-100 mt-1 flex gap-3 p-4`}
                >
                  <CloseIcon className="border-transparent rounded-xl bg-red-alert w-[18px] h-[18px] text-white p-[4px]" />
                  Sorry, this consumer data does not match any of the details
                  provided in our documentation. Please try again with any of
                  the provided consumer credentials.
                </span>
              </div>
            </div>
          )}

          <div className="">
            <div className="border border-grey-stroke-primary p-4 gap-6 flex flex-col mb-10 rounded-lg">
              <img src={CTIDataSharing} className="object-cover" alt="" />
              <div>
                <h2 className="text-trud-black text-3xl font-medium leading-8 mb-2.5">
                  Generate a Consumer Trust Index
                </h2>
                <p className="text-base2 text-grey-content-secondary leading-5 mb-5">
                  For demonstration purposes, the Consumer Trust Index (CTI) can
                  be calculated in our sandbox using data from various sources,
                  including Trudenty's Sandbox, Mastercard, Worldline, and live
                  Open Banking (UK banks only).
                </p>
                <p className="text-base2 text-grey-content-secondary leading-5">
                  Below is a simulation of what happens automatically behind the
                  scenes when using our API. To get started, select a data
                  source and manually input the consumer data into the available
                  fields. Once the Consumer Trust Index is generated, you can
                  explore detailed insights into how the system calculates it.
                </p>
              </div>
            </div>

            <p className="text-lg font-semibold text-grey-content-secondary pb-2.5 leading-7">
              Choose a data source
            </p>
            <ReactListbox
              optionsClass="min-w-full"
              btnClass="w-full rounded-none h-12 !text-body-black border border-stroke bg-white4 mb-4"
              Options={
                ConsumerTypes?.map((type) => ({
                  label: type.title,
                  value: type.value,
                })) ?? []
              }
              selectedOption={consumerType}
              labelClass="text-grey-content-secondary"
              onChange={(selectedOption) => {
                setConsumerType(selectedOption);
              }}
            />
            <div className="p-4 flex bg-grey-650 border border-stroke-primary rounded-lg mb-4 gap-2 mt-4 text-grey-content-secondary">
              <div>
                <GuideIcon />
              </div>
              <span className="font-medium">
                {consumerType === "SANDBOX" ? (
                  <>
                    <h4 className="text-black text-[17.5px]">
                      Manual data entry for consumer to be initialised
                    </h4>
                    Insert consumer data into the form as provided in the{" "}
                    <Link
                      to={
                        "https://docs.trudenty.net/page/initial-data-to-initialise-consumers"
                      }
                      target="_blank"
                      className="underline"
                    >
                      documentation
                    </Link>
                    . If you need to add more than the 5 consumers provided to
                    the sandbox for testing purposes - please let us know.
                  </>
                ) : (
                  "Enter your full name, email and the project key which can be found in the projects settings in the console. To revisit your previous score, ensure you use the same details that were entered during your previous session."
                )}
              </span>
            </div>
            <div className={`px-4 mt-5 `}>
              <Formik
                initialValues={initialConsumer}
                enableReinitialize={true}
                validationSchema={ConsumerInitializeSchema}
                onSubmit={onSubmit}
              >
                {({ values, setFieldValue }) => {
                  return (
                    <Form>
                      <div className="grid grid-cols-12 gap-8">
                        <div className="col-span-12 md:col-span-6">
                          <InputField
                            label="First name"
                            type="text"
                            name="firstName"
                            placeholder="Enter First Name"
                            className="rounded-lg !bg-white border border-grey-content-quaternary text-body-black"
                            isCompulsory={true}
                          />
                        </div>
                        <div className=" col-span-12 md:col-span-6">
                          <InputField
                            label="Last name"
                            type="text"
                            name="lastName"
                            placeholder="Enter Last Name"
                            className="rounded-lg !bg-white border border-grey-content-quaternary text-body-black"
                            isCompulsory={true}
                          />
                        </div>
                        <div className={` col-span-12 md:col-span-12`}>
                          <InputField
                            label="Email"
                            type="text"
                            name="email"
                            placeholder="Enter Email"
                            className="rounded-lg !bg-white border border-grey-content-quaternary text-body-black"
                            isCompulsory={true}
                          />
                        </div>
                        <div className="col-span-12">
                          <ReCAPTCHA
                            sitekey="6Le6UKYqAAAAAB_ktGZhiApNwqWtgz4df1_AmZ8N" // Replace with your site key
                            onChange={(token) => {
                              setFieldValue("recaptcha", token);
                            }}
                          />
                        </div>
                      </div>
                      <div className="mt-8">
                        <Button
                          title="Submit"
                          color="dark3"
                          category="3"
                          type="submit"
                          className="rounded-[4rem] mb-3 px-5 py-3 text-base text-left !w-auto inline-block font-medium "
                          disabled={
                            !isSubmittable(
                              values,
                              {
                                firstName: "",
                                lastName: "",
                                email: "",
                                recaptcha: null,
                              },
                              ["firstName", "lastName", "email", "recaptcha"]
                            )
                          }
                        />
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            </div>
          </div>
        </div>
      </div>
      <SiteFooter classname="absolute 2xl:bottom-auto max-2xl:bottom-auto" />
    </div>
  ) : invalid != null ? (
    <AccessDenied />
  ) : (
    <></>
  );
};

export default Guide;
